import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Building2, Plus, Edit, Trash2, Loader2, FileText, Users, Upload, FileUp, CheckCircle, MapPin, Mail, Hash, AlertTriangle, UserCog, Shield, UserPlus } from 'lucide-react';
import api, { pdvApi } from '../lib/api';
import { PROVINCE_ITALIANE, CITTA_ITALIANE, CODICI_ATECO, FORME_GIURIDICHE } from '../data/italianData';

const INITIAL_FORM = {
  denominazione: '',
  partita_iva: '',
  codice_fiscale: '',
  codice_ateco: '',
  descrizione_ateco: '',
  legale_rappresentante: '',
  sede_legale: '',
  citta: '',
  cap: '',
  provincia: '',
  pec: '',
  telefono: '',
  capitale_sociale: '',
  data_costituzione: '',
  numero_rea: '',
  forma_giuridica: '',
};

export default function AziendaPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [aziende, setAziende] = useState([]);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importedData, setImportedData] = useState(null);
  const [editingAzienda, setEditingAzienda] = useState(null);
  const [importError, setImportError] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const fileInputRef = useRef(null);

  // --- Assignment to admins (only for super_admin) ---
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdminLike = ['super_admin', 'admin', 'amministratore', 'rspp', 'consulente_haccp', 'collaborator', 'studio_tecnico'].includes(user?.role || '');
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // azienda being assigned
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedAdminIds, setSelectedAdminIds] = useState([]);
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [loadingAssign, setLoadingAssign] = useState(false);
  const [savingAssign, setSavingAssign] = useState(false);

  // --- Create azienda-user (login for the company itself) ---
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [createUserTarget, setCreateUserTarget] = useState(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ email: '', password: '', full_name: '', phone: '' });

  // --- Assign aziende to admin's users (verificatori, medici, docenti, etc.) ---
  const [showAssignUsersDialog, setShowAssignUsersDialog] = useState(false);
  const [assignUsersTarget, setAssignUsersTarget] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [selectedAssignedUserIds, setSelectedAssignedUserIds] = useState([]);
  const [loadingAssignUsers, setLoadingAssignUsers] = useState(false);
  const [savingAssignUsers, setSavingAssignUsers] = useState(false);

  useEffect(() => {
    loadAziende();
  }, []);

  const loadAziende = async () => {
    try {
      const response = await api.get('/aziende');
      setAziende(response.data || []);
    } catch (error) {
      try {
        const pdvResponse = await pdvApi.getAll();
        const companies = {};
        pdvResponse.data.forEach(pdv => {
          const companyName = pdv.company_name || 'Azienda non specificata';
          if (!companies[companyName]) {
            companies[companyName] = {
              denominazione: companyName,
              partita_iva: pdv.vat_number || '',
              codice_fiscale: pdv.fiscal_code || '',
              pec: pdv.pec || '',
              pdv_count: 0,
              employees: 0
            };
          }
          companies[companyName].pdv_count++;
          companies[companyName].employees += pdv.employee_count || 0;
        });
        setAziende(Object.values(companies));
      } catch (e) {
        toast.error('Errore nel caricamento delle aziende');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setImportedData(null);
      setImportError(null);
    } else {
      toast.error('Seleziona un file PDF valido');
    }
  };

  const handleImportCamerale = async () => {
    if (!selectedFile) {
      toast.error('Seleziona prima un file PDF');
      return;
    }

    setImporting(true);
    setImportError(null);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedFile);

      const response = await api.post('/aziende/import-camerale', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data) {
        setImportedData(response.data);
        setFormData({
          denominazione: response.data.denominazione || '',
          partita_iva: response.data.partita_iva || '',
          codice_fiscale: response.data.codice_fiscale || '',
          codice_ateco: response.data.codice_ateco || '',
          descrizione_ateco: response.data.descrizione_ateco || '',
          legale_rappresentante: response.data.legale_rappresentante || '',
          sede_legale: response.data.sede_legale || '',
          citta: response.data.citta || '',
          cap: response.data.cap || '',
          provincia: response.data.provincia || '',
          pec: response.data.pec || '',
          telefono: response.data.telefono || '',
          capitale_sociale: response.data.capitale_sociale || '',
          data_costituzione: response.data.data_costituzione || '',
          numero_rea: response.data.numero_rea || '',
          forma_giuridica: response.data.forma_giuridica || '',
        });
        toast.success('Dati estratti con successo dalla visura camerale');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      setImportError(errorMsg);
      if (errorMsg.includes('Libreria PDF') || errorMsg.includes('pdfplumber')) {
        toast.error('Estrazione automatica non disponibile. Inserisci i dati manualmente.');
      } else {
        toast.error('Errore nell\'estrazione: ' + errorMsg);
      }
    } finally {
      setImporting(false);
    }
  };

  const handleSaveAzienda = async () => {
    if (!formData.denominazione) {
      toast.error('La denominazione è obbligatoria');
      return;
    }

    setSaving(true);
    try {
      if (editingAzienda) {
        await api.put(`/aziende/${editingAzienda.id}`, formData);
        toast.success('Azienda aggiornata con successo');
      } else {
        await api.post('/aziende', formData);
        toast.success('Azienda creata con successo');
      }
      setShowImportDialog(false);
      setShowAddDialog(false);
      setShowEditDialog(false);
      resetForm();
      loadAziende();
    } catch (error) {
      toast.error('Errore nel salvataggio: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEditAzienda = (azienda) => {
    setEditingAzienda(azienda);
    setFormData({
      denominazione: azienda.denominazione || '',
      partita_iva: azienda.partita_iva || '',
      codice_fiscale: azienda.codice_fiscale || '',
      codice_ateco: azienda.codice_ateco || '',
      descrizione_ateco: azienda.descrizione_ateco || '',
      legale_rappresentante: azienda.legale_rappresentante || '',
      sede_legale: azienda.sede_legale || '',
      citta: azienda.citta || '',
      cap: azienda.cap || '',
      provincia: azienda.provincia || '',
      pec: azienda.pec || '',
      telefono: azienda.telefono || '',
      capitale_sociale: azienda.capitale_sociale || '',
      data_costituzione: azienda.data_costituzione || '',
      numero_rea: azienda.numero_rea || '',
      forma_giuridica: azienda.forma_giuridica || '',
    });
    setShowEditDialog(true);
  };

  const handleDeleteAzienda = async (azienda) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${azienda.denominazione}"?`)) {
      return;
    }
    try {
      await api.delete(`/aziende/${azienda.id}`);
      toast.success('Azienda eliminata');
      loadAziende();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setSelectedFile(null);
    setImportedData(null);
    setEditingAzienda(null);
    setImportError(null);
  };

  const openAssignDialog = async (azienda) => {
    setAssignTarget(azienda);
    setShowAssignDialog(true);
    setLoadingAssign(true);
    setCreatorInfo(null);
    setSelectedAdminIds(azienda.assigned_admin_ids || []);
    try {
      const [adminsRes, infoRes] = await Promise.all([
        api.get('/users/admins'),
        api.get(`/aziende/${azienda.id}/assigned-admins`),
      ]);
      setAdminUsers(adminsRes.data || []);
      setCreatorInfo(infoRes.data || null);
      const assignedFromServer = (infoRes.data?.assigned_admins || []).map(u => u.id);
      setSelectedAdminIds(assignedFromServer);
    } catch (e) {
      toast.error('Errore caricamento admin: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingAssign(false);
    }
  };

  const toggleAdminId = (id) => {
    setSelectedAdminIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSaveAssignment = async () => {
    if (!assignTarget) return;
    setSavingAssign(true);
    try {
      await api.put(`/aziende/${assignTarget.id}/assigned-admins`, {
        admin_ids: selectedAdminIds,
      });
      toast.success(`Assegnazioni salvate (${selectedAdminIds.length} admin)`);
      setShowAssignDialog(false);
      setAssignTarget(null);
      loadAziende();
    } catch (e) {
      toast.error('Errore salvataggio: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSavingAssign(false);
    }
  };

  const openCreateUserDialog = (azienda) => {
    setCreateUserTarget(azienda);
    setNewUserForm({
      email: '',
      password: '',
      full_name: azienda.denominazione || '',
      phone: azienda.telefono || '',
    });
    setShowCreateUserDialog(true);
  };

  const handleCreateAziendaUser = async () => {
    if (!createUserTarget) return;
    if (!newUserForm.email || !newUserForm.password || !newUserForm.full_name) {
      toast.error('Email, password e nome sono obbligatori');
      return;
    }
    if (newUserForm.password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    setCreatingUser(true);
    try {
      await api.post(`/aziende/${createUserTarget.id}/create-user`, newUserForm);
      toast.success(`Utente azienda creato per "${createUserTarget.denominazione}"`);
      setShowCreateUserDialog(false);
      setCreateUserTarget(null);
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCreatingUser(false);
    }
  };

  const handleMigratePdvLink = async () => {
    if (!window.confirm('Collega automaticamente le UL esistenti alle Aziende per nome/P.IVA?')) return;
    try {
      const r = await api.post('/admin/migrate-pdv-azienda-link');
      toast.success(`Migrazione: ${r.data.matched}/${r.data.total_pdvs_processed} UL collegate (${r.data.unmatched} senza match)`);
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  const openAssignUsersDialog = async (azienda) => {
    setAssignUsersTarget(azienda);
    setShowAssignUsersDialog(true);
    setLoadingAssignUsers(true);
    try {
      const r = await api.get(`/aziende/${azienda.id}/assignable-users`);
      setAssignableUsers(r.data.candidates || []);
      setSelectedAssignedUserIds(r.data.assigned_user_ids || []);
    } catch (e) {
      toast.error('Errore caricamento utenti: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingAssignUsers(false);
    }
  };

  const toggleAssignedUserId = (id) => {
    setSelectedAssignedUserIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSaveAssignedUsers = async () => {
    if (!assignUsersTarget) return;
    setSavingAssignUsers(true);
    try {
      await api.put(`/aziende/${assignUsersTarget.id}/assigned-users`, {
        user_ids: selectedAssignedUserIds,
      });
      toast.success(`Abbinata a ${selectedAssignedUserIds.length} utenti`);
      setShowAssignUsersDialog(false);
      setAssignUsersTarget(null);
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSavingAssignUsers(false);
    }
  };

  const roleLabels = {
    verificatore: 'Verificatore',
    medico_competente: 'Medico Competente',
    docente: 'Docente',
    collaborator: 'Collaboratore',
    gerente: 'Gerente',
    consulente_haccp: 'Consulente HACCP',
    studio_tecnico: 'Studio Tecnico',
  };

  const openImportDialog = () => {
    resetForm();
    setShowImportDialog(true);
  };

  const openAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const switchToManualEntry = () => {
    setImportError(null);
    setImportedData({ manual: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Form fields JSX - defined as variable, not component
  const formFieldsJSX = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
      {/* Denominazione */}
      <div className="md:col-span-2">
        <Label>Denominazione *</Label>
        <Input
          value={formData.denominazione}
          onChange={(e) => handleInputChange('denominazione', e.target.value)}
          placeholder="Es: BONINGROS S.R.L."
        />
      </div>
      
      {/* Partita IVA */}
      <div>
        <Label>Partita IVA</Label>
        <Input
          value={formData.partita_iva}
          onChange={(e) => handleInputChange('partita_iva', e.target.value)}
          placeholder="Es: 03605710833"
          maxLength={11}
        />
      </div>
      
      {/* Codice Fiscale */}
      <div>
        <Label>Codice Fiscale</Label>
        <Input
          value={formData.codice_fiscale}
          onChange={(e) => handleInputChange('codice_fiscale', e.target.value.toUpperCase())}
          placeholder="Es: 03605710833"
          maxLength={16}
        />
      </div>
      
      {/* Codice ATECO */}
      <div className="md:col-span-2">
        <Label>Codice ATECO</Label>
        <div className="flex gap-2">
          <select
            value={formData.codice_ateco}
            onChange={(e) => {
              const selected = CODICI_ATECO.find(a => a.codice === e.target.value);
              handleInputChange('codice_ateco', e.target.value);
              if (selected) {
                handleInputChange('descrizione_ateco', selected.descrizione);
              }
            }}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">Seleziona codice ATECO</option>
            {CODICI_ATECO.map((ateco) => (
              <option key={ateco.codice} value={ateco.codice}>
                {ateco.codice} - {ateco.descrizione.substring(0, 60)}...
              </option>
            ))}
          </select>
        </div>
        <Input
          className="mt-2"
          value={formData.codice_ateco}
          onChange={(e) => handleInputChange('codice_ateco', e.target.value)}
          placeholder="Oppure inserisci manualmente (es: 46.90.00)"
        />
      </div>
      
      {/* Forma Giuridica */}
      <div>
        <Label>Forma Giuridica</Label>
        <select
          value={formData.forma_giuridica}
          onChange={(e) => handleInputChange('forma_giuridica', e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="">Seleziona forma giuridica</option>
          {FORME_GIURIDICHE.map((forma) => (
            <option key={forma} value={forma}>{forma}</option>
          ))}
        </select>
      </div>
      
      {/* Legale Rappresentante */}
      <div>
        <Label>Legale Rappresentante</Label>
        <Input
          value={formData.legale_rappresentante}
          onChange={(e) => handleInputChange('legale_rappresentante', e.target.value)}
          placeholder="Es: Mario Rossi"
        />
      </div>
      
      {/* Sede Legale */}
      <div className="md:col-span-2">
        <Label>Sede Legale (Indirizzo)</Label>
        <Input
          value={formData.sede_legale}
          onChange={(e) => handleInputChange('sede_legale', e.target.value)}
          placeholder="Es: Via Roma 1"
        />
      </div>
      
      {/* Città */}
      <div>
        <Label>Città</Label>
        <Input
          list="citta-list"
          value={formData.citta}
          onChange={(e) => handleInputChange('citta', e.target.value)}
          placeholder="Es: Milano"
        />
        <datalist id="citta-list">
          {CITTA_ITALIANE.slice(0, 100).map((citta) => (
            <option key={citta} value={citta} />
          ))}
        </datalist>
      </div>
      
      {/* CAP */}
      <div>
        <Label>CAP</Label>
        <Input
          value={formData.cap}
          onChange={(e) => handleInputChange('cap', e.target.value)}
          placeholder="Es: 20100"
          maxLength={5}
        />
      </div>
      
      {/* Provincia */}
      <div>
        <Label>Provincia</Label>
        <select
          value={formData.provincia}
          onChange={(e) => handleInputChange('provincia', e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="">Seleziona provincia</option>
          {PROVINCE_ITALIANE.map((prov) => (
            <option key={prov.sigla} value={prov.sigla}>
              {prov.nome} ({prov.sigla})
            </option>
          ))}
        </select>
      </div>
      
      {/* PEC */}
      <div>
        <Label>PEC</Label>
        <Input
          type="email"
          value={formData.pec}
          onChange={(e) => handleInputChange('pec', e.target.value)}
          placeholder="Es: azienda@pec.it"
        />
      </div>
      
      {/* Telefono */}
      <div>
        <Label>Telefono</Label>
        <Input
          value={formData.telefono}
          onChange={(e) => handleInputChange('telefono', e.target.value)}
          placeholder="Es: +39 02 1234567"
        />
      </div>
      
      {/* Capitale Sociale */}
      <div>
        <Label>Capitale Sociale</Label>
        <Input
          value={formData.capitale_sociale}
          onChange={(e) => handleInputChange('capitale_sociale', e.target.value)}
          placeholder="Es: 10.000,00 Euro"
        />
      </div>
      
      {/* Data Costituzione */}
      <div>
        <Label>Data Costituzione</Label>
        <Input
          type="date"
          value={formData.data_costituzione ? 
            (formData.data_costituzione.includes('/') ? 
              formData.data_costituzione.split('/').reverse().join('-') : 
              formData.data_costituzione) : 
            ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value) {
              const [year, month, day] = value.split('-');
              handleInputChange('data_costituzione', `${day}/${month}/${year}`);
            } else {
              handleInputChange('data_costituzione', '');
            }
          }}
        />
      </div>
      
      {/* Numero REA */}
      <div>
        <Label>Numero REA</Label>
        <Input
          value={formData.numero_rea}
          onChange={(e) => handleInputChange('numero_rea', e.target.value)}
          placeholder="Es: ME - 249047"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Azienda
          </h1>
          <p className="text-muted-foreground">Gestione anagrafica aziendale</p>
        </div>
        <div className="flex gap-2">
          {isSuperAdmin && (
            <Button variant="outline" onClick={handleMigratePdvLink} title="Collega UL alle aziende esistenti" data-testid="migrate-pdv-azienda-btn">
              Collega UL ↔ Aziende
            </Button>
          )}
          {isAdminLike && (
            <>
              <Button variant="outline" onClick={openAddDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Nuova Azienda
              </Button>
              <Button onClick={openImportDialog} className="bg-green-600 hover:bg-green-700">
                <FileUp className="w-4 h-4 mr-2" />
                Importa da Camerale
              </Button>
            </>
          )}
        </div>
      </div>

      {aziende.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nessuna azienda registrata</h3>
            <p className="text-muted-foreground mb-4">
              {isAdminLike
                ? "Importa i dati dalla visura camerale o crea manualmente una nuova azienda"
                : "Non risulta ancora nessuna azienda associata al tuo account. Contatta il tuo amministratore."}
            </p>
            {isAdminLike && (
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={openAddDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nuova Azienda
                </Button>
                <Button onClick={openImportDialog} className="bg-green-600 hover:bg-green-700">
                  <FileUp className="w-4 h-4 mr-2" />
                  Importa da Camerale
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {aziende.map((azienda, index) => (
            <Card key={azienda.id || index} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-primary" />
                      {azienda.denominazione || azienda.name}
                    </CardTitle>
                    {azienda.forma_giuridica && (
                      <Badge variant="secondary" className="mt-1">{azienda.forma_giuridica}</Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {isSuperAdmin && azienda.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openAssignDialog(azienda)}
                        title="Assegna admin"
                        data-testid={`assign-admins-btn-${azienda.id}`}
                      >
                        <UserCog className="w-4 h-4 text-blue-600" />
                      </Button>
                    )}
                    {isAdminLike && azienda.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openCreateUserDialog(azienda)}
                        title="Crea utente azienda (login per il cliente)"
                        data-testid={`create-azienda-user-btn-${azienda.id}`}
                      >
                        <Users className="w-4 h-4 text-green-600" />
                      </Button>
                    )}
                    {isAdminLike && azienda.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openAssignUsersDialog(azienda)}
                        title="Abbina questa azienda a verificatori, medici, docenti, studi tecnici..."
                        data-testid={`assign-users-btn-${azienda.id}`}
                      >
                        <UserPlus className="w-4 h-4 text-purple-600" />
                      </Button>
                    )}
                    {isAdminLike && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => handleEditAzienda(azienda)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteAzienda(azienda)} className="text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {/* v55: Logo aziendale — visualizzazione + upload/cambio */}
                {isAdminLike && azienda.id && (
                  <div className="mt-2 flex items-center gap-3 p-2 bg-slate-50 rounded border">
                    {azienda.logo_path ? (
                      <img
                        src={`${api.defaults.baseURL}/aziende/${azienda.id}/logo?t=${azienda.updated_at || Date.now()}`}
                        alt="Logo"
                        className="w-14 h-14 object-contain bg-white border rounded"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-14 h-14 flex items-center justify-center bg-white border border-dashed rounded text-[10px] text-slate-400">no logo</div>
                    )}
                    <div className="flex-1">
                      <p className="text-xs font-medium text-slate-700">Logo aziendale</p>
                      <p className="text-[10px] text-slate-500">PNG/JPG max 2 MB — usato in tutti i documenti</p>
                    </div>
                    <label>
                      <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" data-testid={`upload-azienda-logo-${azienda.id}`}
                        onChange={async (e) => {
                          const f = e.target.files?.[0]; if (!f) return;
                          try {
                            const fd = new FormData(); fd.append('file', f);
                            await api.post(`/aziende/${azienda.id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                            toast.success('Logo caricato');
                            await loadAziende();
                          } catch (err) { toast.error(err.response?.data?.detail || 'Errore upload logo'); }
                          e.target.value = '';
                        }} />
                      <Button asChild size="sm" variant="outline"><span>{azienda.logo_path ? 'Cambia' : 'Carica'}</span></Button>
                    </label>
                    {azienda.logo_path && (
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => {
                        if (!window.confirm('Rimuovere il logo?')) return;
                        try { await api.delete(`/aziende/${azienda.id}/logo`); await loadAziende(); toast.success('Logo rimosso'); }
                        catch { toast.error('Errore'); }
                      }} data-testid={`delete-azienda-logo-${azienda.id}`}><Trash2 className="w-3 h-3" /></Button>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {(azienda.partita_iva || azienda.vat_number) && (
                    <div className="flex items-center gap-1">
                      <Hash className="w-3 h-3 text-muted-foreground" />
                      <span className="text-muted-foreground">P.IVA:</span>
                      <span>{azienda.partita_iva || azienda.vat_number}</span>
                    </div>
                  )}
                  {(azienda.codice_fiscale || azienda.fiscal_code) && (
                    <div className="flex items-center gap-1">
                      <Hash className="w-3 h-3 text-muted-foreground" />
                      <span className="text-muted-foreground">C.F.:</span>
                      <span>{azienda.codice_fiscale || azienda.fiscal_code}</span>
                    </div>
                  )}
                  {azienda.codice_ateco && (
                    <div className="col-span-2 flex items-center gap-1">
                      <FileText className="w-3 h-3 text-muted-foreground" />
                      <span className="text-muted-foreground">ATECO:</span>
                      <span>{azienda.codice_ateco}</span>
                    </div>
                  )}
                  {azienda.legale_rappresentante && (
                    <div className="col-span-2 flex items-center gap-1">
                      <Users className="w-3 h-3 text-muted-foreground" />
                      <span className="text-muted-foreground">Legale Rapp.:</span>
                      <span>{azienda.legale_rappresentante}</span>
                    </div>
                  )}
                  {(azienda.sede_legale || azienda.citta) && (
                    <div className="col-span-2 flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-muted-foreground" />
                      <span className="text-muted-foreground">Sede:</span>
                      <span className="truncate">
                        {azienda.sede_legale}
                        {azienda.citta && `, ${azienda.citta}`}
                        {azienda.provincia && ` (${azienda.provincia})`}
                      </span>
                    </div>
                  )}
                  {azienda.pec && (
                    <div className="col-span-2 flex items-center gap-1">
                      <Mail className="w-3 h-3 text-muted-foreground" />
                      <span className="text-muted-foreground">PEC:</span>
                      <span>{azienda.pec}</span>
                    </div>
                  )}
                  {azienda.capitale_sociale && (
                    <div className="flex items-center gap-1">
                      <Hash className="w-3 h-3 text-muted-foreground" />
                      <span className="text-muted-foreground">Capitale:</span>
                      <span>{azienda.capitale_sociale}</span>
                    </div>
                  )}
                  {azienda.data_costituzione && (
                    <div className="flex items-center gap-1">
                      <Hash className="w-3 h-3 text-muted-foreground" />
                      <span className="text-muted-foreground">Costituz.:</span>
                      <span>{azienda.data_costituzione}</span>
                    </div>
                  )}
                  {azienda.numero_rea && (
                    <div className="col-span-2 flex items-center gap-1">
                      <Hash className="w-3 h-3 text-muted-foreground" />
                      <span className="text-muted-foreground">REA:</span>
                      <span>{azienda.numero_rea}</span>
                    </div>
                  )}
                </div>
                {(azienda.pdv_count || azienda.employees) && (
                  <div className="flex items-center gap-4 pt-2 border-t">
                    {azienda.pdv_count > 0 && (
                      <div className="flex items-center gap-1 text-sm">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span>{azienda.pdv_count} UL</span>
                      </div>
                    )}
                    {azienda.employees > 0 && (
                      <div className="flex items-center gap-1 text-sm">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span>{azienda.employees} dipendenti</span>
                      </div>
                    )}
                  </div>
                )}
                {(azienda.created_by_name || (azienda.assigned_admin_ids && azienda.assigned_admin_ids.length > 0)) && (
                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t text-xs">
                    {azienda.created_by_name && (
                      <Badge variant="outline" className="gap-1">
                        <Shield className="w-3 h-3" />
                        Creata da: {azienda.created_by_name}
                      </Badge>
                    )}
                    {azienda.assigned_admin_ids && azienda.assigned_admin_ids.length > 0 && (
                      <Badge variant="secondary" className="gap-1">
                        <UserCog className="w-3 h-3" />
                        {azienda.assigned_admin_ids.length} admin assegnati
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Import from Camerale Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5 text-green-600" />
              Importa Dati da Visura Camerale
            </DialogTitle>
            <DialogDescription>
              Carica un file PDF della visura camerale per estrarre automaticamente i dati aziendali
            </DialogDescription>
          </DialogHeader>

          {!importedData && !importError ? (
            <div className="space-y-4">
              <div 
                className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                {selectedFile ? (
                  <div>
                    <p className="font-medium text-primary">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {(selectedFile.size / 1024).toFixed(1)} KB - Clicca per cambiare file
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium">Clicca per selezionare un file PDF</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Formati supportati: PDF della visura camerale
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : importError ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 text-amber-600 bg-amber-50 p-4 rounded-lg">
                <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Impossibile estrarre automaticamente i dati</p>
                  <p className="text-sm mt-1">{importError}</p>
                </div>
              </div>
              <div className="text-center">
                <Button onClick={switchToManualEntry} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Inserisci Manualmente
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">
                  {importedData?.manual ? 'Inserisci i dati manualmente' : 'Dati estratti! Verifica e modifica se necessario.'}
                </span>
              </div>
              {formFieldsJSX}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Annulla
            </Button>
            {!importedData && !importError ? (
              <Button 
                onClick={handleImportCamerale} 
                disabled={!selectedFile || importing}
                className="bg-green-600 hover:bg-green-700"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Estrazione in corso...
                  </>
                ) : (
                  <>
                    <FileUp className="w-4 h-4 mr-2" />
                    Estrai Dati
                  </>
                )}
              </Button>
            ) : importedData ? (
              <Button onClick={handleSaveAzienda} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Salva Azienda
                  </>
                )}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Azienda Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Nuova Azienda
            </DialogTitle>
            <DialogDescription>
              Inserisci manualmente i dati dell'azienda
            </DialogDescription>
          </DialogHeader>
          {formFieldsJSX}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveAzienda} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Salva Azienda
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Azienda Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modifica Azienda
            </DialogTitle>
            <DialogDescription>
              Modifica i dati dell'azienda
            </DialogDescription>
          </DialogHeader>
          {formFieldsJSX}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveAzienda} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Salva Modifiche
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Admins Dialog (super_admin only) */}
      <Dialog open={showAssignDialog} onOpenChange={(o) => { if (!o) { setShowAssignDialog(false); setAssignTarget(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="assign-admins-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-blue-600" />
              Assegna amministratori
            </DialogTitle>
            <DialogDescription>
              {assignTarget?.denominazione
                ? `Seleziona gli amministratori che potranno gestire "${assignTarget.denominazione}".`
                : 'Seleziona gli amministratori che potranno gestire questa azienda.'}
            </DialogDescription>
          </DialogHeader>

          {loadingAssign ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-4">
              {creatorInfo?.creator && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-600" />
                  <span>
                    <strong>Creatore:</strong> {creatorInfo.creator.full_name}{' '}
                    <span className="text-muted-foreground">({creatorInfo.creator.email})</span>
                  </span>
                </div>
              )}

              {adminUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Nessun utente con ruolo &quot;admin&quot; disponibile.
                </p>
              ) : (
                <div className="max-h-[50vh] overflow-y-auto space-y-1 border rounded-md p-2">
                  {adminUsers.map((adm) => {
                    const checked = selectedAdminIds.includes(adm.id);
                    return (
                      <label
                        key={adm.id}
                        htmlFor={`assign-admin-${adm.id}`}
                        className={`flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-accent ${checked ? 'bg-blue-50' : ''}`}
                        data-testid={`assign-admin-row-${adm.id}`}
                      >
                        <input
                          id={`assign-admin-${adm.id}`}
                          type="checkbox"
                          className="h-4 w-4 accent-primary"
                          checked={checked}
                          onChange={() => toggleAdminId(adm.id)}
                          data-testid={`assign-admin-checkbox-${adm.id}`}
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{adm.full_name || '(senza nome)'}</div>
                          <div className="text-xs text-muted-foreground">{adm.email}</div>
                        </div>
                        {checked && (
                          <Badge variant="default" className="bg-blue-600">
                            Selezionato
                          </Badge>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Gli admin selezionati vedranno questa azienda nella loro lista e potranno gestirla.
                Il super admin vede sempre tutte le aziende.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAssignDialog(false); setAssignTarget(null); }}>
              Annulla
            </Button>
            <Button
              onClick={handleSaveAssignment}
              disabled={savingAssign || loadingAssign}
              data-testid="save-assignment-btn"
            >
              {savingAssign ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Salva assegnazioni ({selectedAdminIds.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Create Azienda User Dialog (admin/super_admin) */}
      <Dialog open={showCreateUserDialog} onOpenChange={(o) => { if (!o) { setShowCreateUserDialog(false); setCreateUserTarget(null); } }}>
        <DialogContent className="max-w-lg" data-testid="create-azienda-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              Crea utente azienda
            </DialogTitle>
            <DialogDescription>
              {createUserTarget?.denominazione
                ? `Crea le credenziali di accesso per "${createUserTarget.denominazione}". L'utente potrà gestire solo le proprie Unità Locali.`
                : "Crea le credenziali di accesso per questa azienda."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome completo / Riferimento *</Label>
              <Input
                value={newUserForm.full_name}
                onChange={(e) => setNewUserForm({ ...newUserForm, full_name: e.target.value })}
                placeholder="Es: Mario Rossi"
                data-testid="azienda-user-fullname"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUserForm.email}
                onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                placeholder="azienda@example.com"
                autoComplete="off"
                data-testid="azienda-user-email"
              />
            </div>
            <div>
              <Label>Password * (min 6 caratteri)</Label>
              <Input
                type="password"
                value={newUserForm.password}
                onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                placeholder="••••••••"
                autoComplete="new-password"
                data-testid="azienda-user-password"
              />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input
                value={newUserForm.phone}
                onChange={(e) => setNewUserForm({ ...newUserForm, phone: e.target.value })}
                placeholder="+39 ..."
              />
            </div>
            <p className="text-xs text-muted-foreground">
              L'utente avrà ruolo <strong>azienda</strong>: vedrà solo le UL, personale, documenti
              e scadenze legati a questa azienda.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateUserDialog(false); setCreateUserTarget(null); }}>
              Annulla
            </Button>
            <Button
              onClick={handleCreateAziendaUser}
              disabled={creatingUser}
              className="bg-green-600 hover:bg-green-700"
              data-testid="create-azienda-user-submit"
            >
              {creatingUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creazione...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Crea utente
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Assign Users (verificatori, medici, docenti) Dialog */}
      <Dialog open={showAssignUsersDialog} onOpenChange={(o) => { if (!o) { setShowAssignUsersDialog(false); setAssignUsersTarget(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="assign-users-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              Abbinà azienda a utenti
            </DialogTitle>
            <DialogDescription>
              {assignUsersTarget?.denominazione
                ? `Seleziona gli utenti che vedranno l'azienda "${assignUsersTarget.denominazione}" e tutte le sue UL.`
                : "Seleziona gli utenti che vedranno l'azienda e tutte le sue UL."}
            </DialogDescription>
          </DialogHeader>

          {loadingAssignUsers ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-3">
              {assignableUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Non hai ancora utenti (verificatori, medici, docenti, collaboratori, studi tecnici) da abbinare.
                  Creali prima dalla pagina "Utenti".
                </p>
              ) : (
                <div className="max-h-[55vh] overflow-y-auto space-y-1 border rounded-md p-2">
                  {assignableUsers.map((u) => {
                    const checked = selectedAssignedUserIds.includes(u.id);
                    return (
                      <label
                        key={u.id}
                        htmlFor={`assign-user-${u.id}`}
                        className={`flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-accent ${checked ? 'bg-purple-50' : ''}`}
                        data-testid={`assign-user-row-${u.id}`}
                      >
                        <input
                          id={`assign-user-${u.id}`}
                          type="checkbox"
                          className="h-4 w-4 accent-primary"
                          checked={checked}
                          onChange={() => toggleAssignedUserId(u.id)}
                          data-testid={`assign-user-checkbox-${u.id}`}
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm flex items-center gap-2">
                            {u.full_name || '(senza nome)'}
                            <Badge variant="outline" className="text-[10px]">
                              {roleLabels[u.role] || u.role}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">{u.email}</div>
                        </div>
                        {checked && <Badge className="bg-purple-600">Abbinato</Badge>}
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Gli utenti abbinati vedranno questa azienda e <strong>tutte le sue UL</strong> automaticamente.
                Per i Verificatori/Medici/Docenti puoi alternativamente assegnare solo singole UL dalla pagina "Utenti".
                <br/>
                <strong className="text-indigo-700">Studi Tecnici:</strong> oltre alla visibilità, l'azienda viene aggiunta automaticamente come <strong>Cliente Studio</strong> e diventa disponibile nel wizard "Deleghe (DPR 445/2000)".
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAssignUsersDialog(false); setAssignUsersTarget(null); }}>
              Annulla
            </Button>
            <Button
              onClick={handleSaveAssignedUsers}
              disabled={savingAssignUsers || loadingAssignUsers || assignableUsers.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-assigned-users-btn"
            >
              {savingAssignUsers ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Salva abbinamenti ({selectedAssignedUserIds.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
